import { useState, useRef, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { opportunitiesService } from '@/services/opportunities-service'
import { usersService } from '@/services/users-service'
import { AddressAutocomplete } from '@/components/shared/address-autocomplete'
import type { User } from '@/types'

const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD',
  'MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC',
  'SD','TN','TX','UT','VT','VA','WA','WV','WI','WY',
]

const WORK_TYPES = [
  'New Paving','Removal/Replacement','Concrete','Sealcoating','Striping',
  'Overlays','Patching','ADA Compliance','Pressure Washing','Graffiti Removal','Other or Unsure',
]

function formatPhone(value: string): string {
  const digits = value.replace(/\D/g, '').slice(0, 10)
  if (digits.length <= 3) return digits
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`
}

/* ── Icons ── */
const ManualIcon = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 20h9" /><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
  </svg>
)

const UrlIcon = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
    <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
  </svg>
)

const PdfIcon = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
    <line x1="9" y1="13" x2="15" y2="13" /><line x1="9" y1="17" x2="12" y2="17" />
  </svg>
)

const UploadIcon = () => (
  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="17 8 12 3 7 8" />
    <line x1="12" y1="3" x2="12" y2="15" />
  </svg>
)

const FileIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
  </svg>
)

const RemoveIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
  </svg>
)

type Source = 'manual' | 'url' | 'pdf'
type Step = 'source' | 'form'

export function OpportunityCreatePage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  // If ?source= is set, skip the card selector and jump straight to the panel
  const presetSource = searchParams.get('source') as Source | null
  const skipCards = presetSource === 'manual' || presetSource === 'pdf' || presetSource === 'url'

  const [step, setStep] = useState<Step>(() => presetSource === 'manual' ? 'form' : 'source')
  const [source, setSource] = useState<Source>(() => presetSource ?? 'manual')

  /* URL source */
  const [urlInput, setUrlInput] = useState('')

  /* PDF source */
  const [pdfFile, setPdfFile] = useState<File | null>(null)
  const [dragOver, setDragOver] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  /* Extracting state */
  const [extracting, setExtracting] = useState(false)
  const [extractError, setExtractError] = useState('')

  /* Manual form fields */
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [phone, setPhone] = useState('')
  const [email, setEmail] = useState('')
  const [address, setAddress] = useState('')
  const [city, setCity] = useState('')
  const [state, setState] = useState('')
  const [zip, setZip] = useState('')
  const [workType, setWorkType] = useState('')
  const [scopeOfWork, setScopeOfWork] = useState('')
  const [estimatedBudget, setEstimatedBudget] = useState('')
  const [dueDate, setDueDate] = useState('')
  const [lead, setLead] = useState('')
  const [estimatorNotes, setEstimatorNotes] = useState('')

  /* Assignment */
  const [assignedTo, setAssignedTo] = useState('')
  const [brief, setBrief] = useState('')
  const [users, setUsers] = useState<User[]>([])

  /* UI */
  const [saving, setSaving] = useState(false)
  const [formError, setFormError] = useState('')

  useEffect(() => {
    usersService.list().then(setUsers).catch(() => {})
  }, [])

  /* ── Source selection ── */
  function handleSourceSelect(s: Source) {
    setSource(s)
    if (s === 'manual') setStep('form')
    // URL and PDF stay on source step to collect the URL/file first
  }

  /* ── PDF helpers ── */
  function handleFileSelect(file: File) {
    if (file.type !== 'application/pdf') { setExtractError('Only PDF files are accepted'); return }
    if (file.size > 25 * 1024 * 1024) { setExtractError('File must be under 25 MB'); return }
    setPdfFile(file)
    setExtractError('')
  }

  /* ── Extract from URL ── */
  async function handleExtractUrl() {
    if (!urlInput.trim()) { setExtractError('Please enter a URL'); return }
    try {
      setExtracting(true)
      setExtractError('')
      const opp = await opportunitiesService.fromUrl(urlInput.trim())
      navigate(`/opportunities/${opp.id}`)
    } catch {
      setExtractError('Failed to extract data from that URL. Check the address and try again.')
    } finally {
      setExtracting(false)
    }
  }

  /* ── Extract from PDF ── */
  async function handleExtractPdf() {
    if (!pdfFile) { setExtractError('Please select a PDF file first'); return }

    // Vercel serverless has a ~4.5 MB request body limit; base64 adds ~33%
    const maxFileSizeMB = 3.2
    const fileSizeMB = pdfFile.size / (1024 * 1024)
    if (fileSizeMB > maxFileSizeMB) {
      setExtractError(`PDF is too large (${fileSizeMB.toFixed(1)} MB). Maximum size is ${maxFileSizeMB} MB. Try a smaller or compressed PDF.`)
      return
    }

    try {
      setExtracting(true)
      setExtractError('')
      const opp = await opportunitiesService.uploadPdf(pdfFile)
      navigate(`/opportunities/${opp.id}`)
    } catch (err: any) {
      const serverMsg = err?.response?.data?.message
      if (serverMsg) {
        setExtractError(`PDF extraction failed: ${serverMsg}`)
      } else if (err?.message?.includes('413') || err?.response?.status === 413) {
        setExtractError('PDF is too large for the server. Try a smaller or compressed PDF.')
      } else if (err?.message?.includes('timeout') || err?.code === 'ECONNABORTED') {
        setExtractError('Request timed out. The PDF may be too large or complex. Try a smaller file.')
      } else {
        setExtractError('Failed to extract data from that PDF. Please try again.')
      }
    } finally {
      setExtracting(false)
    }
  }

  /* ── Save manual opportunity ── */
  async function handleSave() {
    if (!firstName.trim() || !lastName.trim()) { setFormError('First and last name are required'); return }
    if (!workType) { setFormError('Please select a type of work'); return }

    try {
      setSaving(true)
      setFormError('')

      const projectAddress = [address.trim(), city.trim(), state, zip.trim()].filter(Boolean).join(', ')

      const opp = await opportunitiesService.createManual({
        title: `${firstName.trim()} ${lastName.trim()} — ${workType}`,
        scopeOfWork: scopeOfWork.trim() || undefined,
        projectAddress: projectAddress || undefined,
        projectTypes: workType ? [workType] : [],
        contactName: `${firstName.trim()} ${lastName.trim()}`,
        contactEmail: email.trim() || undefined,
        contactPhone: phone.trim() || undefined,
        estimatedBudget: estimatedBudget ? parseFloat(estimatedBudget) : undefined,
        dueDate: dueDate || undefined,
        lead: lead.trim() || undefined,
        estimatorNotes: estimatorNotes.trim() || undefined,
      })

      /* Assign with brief if an estimator was selected */
      if (assignedTo) {
        await opportunitiesService.assign(opp.id, assignedTo, brief.trim() || undefined)
      }

      navigate(`/opportunities/${opp.id}`)
    } catch {
      setFormError('Failed to create opportunity. Please try again.')
    } finally {
      setSaving(false)
    }
  }

  /* ══════════════════════════════════════════════════
     RENDER: Source Selection
  ══════════════════════════════════════════════════ */
  if (step === 'source') {
    return (
      <div className="opp-create-page">
        <div className="opp-create-header">
          <h1 className="opp-create-title">
            {skipCards && source === 'pdf' ? 'From PDF' : skipCards && source === 'url' ? 'From URL' : 'Add Opportunity'}
          </h1>
          <p className="opp-create-subtitle">
            {skipCards && source === 'pdf'
              ? 'Upload an RFQ or bid document and AI will extract the scope, address, and details.'
              : skipCards && source === 'url'
              ? 'Paste a link to a bid posting or RFQ page and AI will extract the details.'
              : 'Choose how you want to add this opportunity.'}
          </p>
        </div>

        {/* Source cards — hidden when arriving via dropdown shortcut */}
        {!skipCards && <div className="opp-create-source-grid">
          <button
            className={`opp-create-source-card ${source === 'manual' ? 'opp-create-source-card-active' : ''}`}
            onClick={() => handleSourceSelect('manual')}
          >
            <div className="opp-create-source-icon"><ManualIcon /></div>
            <div className="opp-create-source-label">Manual Entry</div>
            <div className="opp-create-source-desc">Enter contact info and project details from a phone call or walk-in.</div>
          </button>

          <button
            className={`opp-create-source-card ${source === 'url' ? 'opp-create-source-card-active' : ''}`}
            onClick={() => { setSource('url'); setExtractError('') }}
          >
            <div className="opp-create-source-icon"><UrlIcon /></div>
            <div className="opp-create-source-label">From URL</div>
            <div className="opp-create-source-desc">Paste a link to a bid posting or RFQ page and AI will extract the details.</div>
          </button>

          <button
            className={`opp-create-source-card ${source === 'pdf' ? 'opp-create-source-card-active' : ''}`}
            onClick={() => { setSource('pdf'); setExtractError('') }}
          >
            <div className="opp-create-source-icon"><PdfIcon /></div>
            <div className="opp-create-source-label">From PDF</div>
            <div className="opp-create-source-desc">Upload an RFQ or bid document and AI will extract the scope, address, and details.</div>
          </button>
        </div>}

        {/* URL input panel */}
        {source === 'url' && (
          <div className="opp-create-extract-panel">
            <label className="rfp-label">Bid or RFQ URL</label>
            <div className="opp-create-url-row">
              <input
                className="rfp-input opp-create-url-input"
                value={urlInput}
                onChange={(e) => setUrlInput(e.target.value)}
                placeholder="https://..."
                onKeyDown={(e) => e.key === 'Enter' && handleExtractUrl()}
                autoFocus
                disabled={extracting}
              />
              <button
                className="rfp-btn-primary opp-create-extract-btn"
                onClick={handleExtractUrl}
                disabled={extracting || !urlInput.trim()}
              >
                {extracting ? (
                  <><div className="opp-create-spinner" /> Extracting...</>
                ) : 'Extract'}
              </button>
            </div>
            {extractError && <p className="rfp-error" style={{ marginTop: 8 }}>{extractError}</p>}
            {extracting && (
              <p className="opp-create-extract-hint">
                AI is reading the page and extracting the scope, address, and contact details. This takes 10–20 seconds.
              </p>
            )}
          </div>
        )}

        {/* PDF upload panel */}
        {source === 'pdf' && (
          <div className="opp-create-extract-panel">
            {pdfFile ? (
              <div className="rfp-create-file-card">
                <div className="rfp-create-file-info">
                  <FileIcon />
                  <div>
                    <p className="rfp-create-file-name">{pdfFile.name}</p>
                    <p className="rfp-create-file-size">{(pdfFile.size / 1024 / 1024).toFixed(1)} MB</p>
                  </div>
                </div>
                <button className="rfp-create-file-remove" onClick={() => setPdfFile(null)} disabled={extracting}>
                  <RemoveIcon />
                </button>
              </div>
            ) : (
              <div
                className={`rfp-create-dropzone ${dragOver ? 'rfp-create-dropzone-active' : ''}`}
                onClick={() => fileInputRef.current?.click()}
                onDrop={(e) => { e.preventDefault(); setDragOver(false); const f = e.dataTransfer.files[0]; if (f) handleFileSelect(f) }}
                onDragOver={(e) => { e.preventDefault(); setDragOver(true) }}
                onDragLeave={() => setDragOver(false)}
              >
                <UploadIcon />
                <p className="rfp-create-dropzone-text">Drop a PDF here or <span className="rfp-create-dropzone-link">browse</span></p>
                <p className="rfp-create-dropzone-hint">PDF only, max 25 MB</p>
              </div>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,application/pdf"
              className="rfp-create-file-input"
              onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFileSelect(f); e.target.value = '' }}
            />
            {pdfFile && (
              <button
                className="rfp-btn-primary opp-create-extract-btn"
                style={{ marginTop: 12 }}
                onClick={handleExtractPdf}
                disabled={extracting}
              >
                {extracting ? (
                  <><div className="opp-create-spinner" /> Extracting...</>
                ) : 'Extract from PDF'}
              </button>
            )}
            {extractError && <p className="rfp-error" style={{ marginTop: 8 }}>{extractError}</p>}
            {extracting && (
              <p className="opp-create-extract-hint">
                AI is reading the document and extracting the scope, address, and contact details. This takes 15–30 seconds.
              </p>
            )}
          </div>
        )}
      </div>
    )
  }

  /* ══════════════════════════════════════════════════
     RENDER: Manual Form
  ══════════════════════════════════════════════════ */
  return (
    <div className="rfp-create">
      {/* Header */}
      <div className="rfp-create-header">
        <h1 className="rfp-create-title">New Opportunity</h1>
        <p className="rfp-create-subtitle">Fill in the contact and project details to create this opportunity.</p>
      </div>

      {formError && <p className="rfp-error" style={{ marginBottom: 16 }}>{formError}</p>}

      <div className="rfp-create-grid">
        {/* ── Left Column: Form ── */}
        <div className="rfp-create-main">

          {/* Contact Information */}
          <div className="rfp-create-section">
            <h3 className="rfp-create-section-title">Contact Information</h3>
            <div className="rfp-row-2">
              <div className="rfp-field">
                <label className="rfp-label">First Name <span className="rfp-required">*</span></label>
                <input className="rfp-input" value={firstName} onChange={(e) => setFirstName(e.target.value)} placeholder="John" autoFocus />
              </div>
              <div className="rfp-field">
                <label className="rfp-label">Last Name <span className="rfp-required">*</span></label>
                <input className="rfp-input" value={lastName} onChange={(e) => setLastName(e.target.value)} placeholder="Smith" />
              </div>
            </div>
            <div className="rfp-row-2">
              <div className="rfp-field">
                <label className="rfp-label">Phone Number</label>
                <input className="rfp-input" value={phone} onChange={(e) => setPhone(formatPhone(e.target.value))} placeholder="(555) 123-4567" type="tel" />
              </div>
              <div className="rfp-field">
                <label className="rfp-label">Email</label>
                <input className="rfp-input" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="john@example.com" type="email" />
              </div>
            </div>
          </div>

          {/* Project Address */}
          <div className="rfp-create-section">
            <h3 className="rfp-create-section-title">Project Address</h3>
            <div className="rfp-field">
              <label className="rfp-label">Street Address</label>
              <AddressAutocomplete
                value={address}
                onChange={setAddress}
                onSelect={(_street, c, s, z) => { setCity(c); setState(s); setZip(z) }}
                placeholder="Start typing an address..."
              />
            </div>
            <div className="rfp-row-3">
              <div className="rfp-field">
                <label className="rfp-label">City</label>
                <input className="rfp-input" value={city} onChange={(e) => setCity(e.target.value)} placeholder="Springfield" />
              </div>
              <div className="rfp-field">
                <label className="rfp-label">State</label>
                <select className="rfp-select" value={state} onChange={(e) => setState(e.target.value)}>
                  <option value="">—</option>
                  {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div className="rfp-field">
                <label className="rfp-label">Zipcode</label>
                <input className="rfp-input" value={zip} onChange={(e) => setZip(e.target.value.replace(/\D/g, '').slice(0, 5))} placeholder="62701" inputMode="numeric" />
              </div>
            </div>
          </div>

          {/* Project Details */}
          <div className="rfp-create-section">
            <h3 className="rfp-create-section-title">Project Details</h3>
            <div className="rfp-field">
              <label className="rfp-label">Type of Work <span className="rfp-required">*</span></label>
              <select className="rfp-select" value={workType} onChange={(e) => setWorkType(e.target.value)}>
                <option value="">— Select Type of Work —</option>
                {WORK_TYPES.map((w) => <option key={w} value={w}>{w}</option>)}
              </select>
            </div>
            <div className="rfp-field">
              <label className="rfp-label">Scope of Work</label>
              <textarea className="rfp-textarea" value={scopeOfWork} onChange={(e) => setScopeOfWork(e.target.value)} placeholder="Describe the scope of work..." rows={4} />
            </div>
            <div className="rfp-row-2">
              <div className="rfp-field">
                <label className="rfp-label">Estimated Budget</label>
                <input className="rfp-input" value={estimatedBudget} onChange={(e) => setEstimatedBudget(e.target.value)} placeholder="$0" type="number" min="0" />
              </div>
              <div className="rfp-field">
                <label className="rfp-label">Due Date</label>
                <input className="rfp-input" value={dueDate} onChange={(e) => setDueDate(e.target.value)} type="date" />
              </div>
            </div>
            <div className="rfp-field">
              <label className="rfp-label" htmlFor="lead-input">Lead</label>
              <input
                id="lead-input"
                type="text"
                maxLength={80}
                className="rfp-input"
                value={lead}
                onChange={(e) => setLead(e.target.value)}
                placeholder="e.g., PC, Kym, Cold call"
              />
              <p className="opp-create-assign-hint" style={{ marginTop: 6 }}>How this lead came in</p>
            </div>
            <div className="rfp-field">
              <label className="rfp-label" htmlFor="estimator-notes-input">Notes</label>
              <textarea
                id="estimator-notes-input"
                rows={3}
                className="rfp-textarea"
                value={estimatorNotes}
                onChange={(e) => setEstimatorNotes(e.target.value)}
                placeholder="e.g., walked site 5/13, waiting on plans"
              />
              <p className="opp-create-assign-hint" style={{ marginTop: 6 }}>Status notes visible in the Opportunity Queue report</p>
            </div>
          </div>

          {/* Footer Actions */}
          <div className="rfp-create-footer">
            <button className="rfp-btn-secondary" onClick={() => navigate('/opportunities')} disabled={saving}>Cancel</button>
            <button className="rfp-btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? 'Creating...' : 'Create Opportunity'}
            </button>
          </div>
        </div>

        {/* ── Right Column: Assign & Brief ── */}
        <div className="rfp-create-sidebar">
          <div className="rfp-create-section">
            <h3 className="rfp-create-section-title">Assign to Estimator</h3>
            <p className="opp-create-assign-hint">Optional — you can assign later from the opportunity page.</p>
            <div className="rfp-assign-dropdown">
              <select className="rfp-select" value={assignedTo} onChange={(e) => setAssignedTo(e.target.value)}>
                <option value="">— Unassigned —</option>
                {users.map((u) => <option key={u.id} value={u.id}>{u.displayName}</option>)}
              </select>
              {assignedTo && (() => {
                const user = users.find((u) => u.id === assignedTo)
                if (!user) return null
                return (
                  <div className="rfp-assign-preview">
                    {user.avatarUrl ? (
                      <img src={user.avatarUrl} alt="" className="rfp-assign-avatar" />
                    ) : (
                      <div className="rfp-assign-avatar-placeholder">{user.displayName.charAt(0).toUpperCase()}</div>
                    )}
                    <div>
                      <p className="rfp-assign-name">{user.displayName}</p>
                      <p className="rfp-assign-role">{user.role}</p>
                    </div>
                  </div>
                )
              })()}
            </div>
          </div>

          {/* Brief — only shown when an estimator is selected */}
          {assignedTo && (
            <div className="rfp-create-section">
              <h3 className="rfp-create-section-title">Handoff Brief</h3>
              <p className="opp-create-assign-hint">This will appear as the first note in the thread so the estimator sees it when they open the opportunity.</p>
              <textarea
                className="rfp-create-textarea"
                value={brief}
                onChange={(e) => setBrief(e.target.value)}
                placeholder="Add context, priorities, or anything the estimator should know..."
                rows={5}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
